import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import {
  DownloadIcon,
  ChevronDownIcon,
  EllipsisIcon,
  MapPinIcon,
  PencilIcon,
  PowerIcon,
  PlusIcon,
  SearchIcon,
  Settings2Icon,
  XIcon,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  apiBaseUrl,
  authClient,
  listRegions,
  listUserRegions,
  updateRegion,
  type RegionRecord,
} from '@/lib/auth-client';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { SortHeaderButton, ToolbarButton, TruncatedValue } from '@/components/ui/table-shared';
import { UsersTableColumnsPopover, type UsersTableColumnOption } from '@/components/users-table-columns-popover';
import { ConfirmationModal } from './confirmation-modal';
import { RegionForm } from './region-form';

type SessionData = NonNullable<ReturnType<typeof authClient.useSession>['data']>;
type RegionsManagementPageProps = {
  session: SessionData;
};
type RegionSortPreset = 'name:asc' | 'name:desc' | 'code:asc' | 'status:asc' | 'commercialCount:desc';
type RegionStatusFilter = 'all' | RegionRecord['status'];
type RegionTableColumnKey = 'name' | 'code' | 'commercialCount' | 'status';

const pageSizeOptions = [20, 50, 100] as const;
const REGIONS_TABLE_COLUMNS_STORAGE_KEY = 'vibecode-admin-regions-table-columns';

const sortPresetOptions: Array<{ label: string; value: RegionSortPreset }> = [
  { label: 'Ordre personnalisé', value: 'name:asc' },
  { label: 'Nom (A-Z)', value: 'name:asc' },
  { label: 'Nom (Z-A)', value: 'name:desc' },
  { label: 'Code (A-Z)', value: 'code:asc' },
  { label: 'Actives d’abord', value: 'status:asc' },
  { label: 'Commerciaux d’abord', value: 'commercialCount:desc' },
];

const statusFilterOptions: Array<{ label: string; value: RegionStatusFilter }> = [
  { label: 'Tous les statuts', value: 'all' },
  { label: 'Actives', value: 'Active' },
  { label: 'Inactives', value: 'Inactive' },
];

const defaultTableColumns: Array<UsersTableColumnOption & { key: RegionTableColumnKey }> = [
  { key: 'name', label: 'Nom de la région', visible: true },
  { key: 'code', label: 'Code', visible: true },
  { key: 'commercialCount', label: 'Commerciaux', visible: true },
  { key: 'status', label: 'Statut', visible: true },
];

function getPaginationItems(page: number, pageCount: number) {
  if (pageCount <= 7) {
    return Array.from({ length: pageCount }, (_, index) => index + 1);
  }

  const pages = new Set<number>([1, pageCount, page, page - 1, page + 1]);
  if (page <= 3) {
    pages.add(2);
    pages.add(3);
    pages.add(4);
  }
  if (page >= pageCount - 2) {
    pages.add(pageCount - 1);
    pages.add(pageCount - 2);
    pages.add(pageCount - 3);
  }

  return Array.from(pages).filter((value) => value >= 1 && value <= pageCount).sort((left, right) => left - right);
}

function readStoredTableColumns() {
  if (typeof window === 'undefined') {
    return defaultTableColumns;
  }

  try {
    const raw = window.localStorage.getItem(REGIONS_TABLE_COLUMNS_STORAGE_KEY);
    if (!raw) return defaultTableColumns;
    const parsed = JSON.parse(raw) as Array<Partial<UsersTableColumnOption> & { key?: string }>;
    const defaults = new Map(defaultTableColumns.map((column) => [column.key, column]));
    const normalized = parsed
      .map((column) => {
        if (!column.key) return null;
        const defaultColumn = defaults.get(column.key as RegionTableColumnKey);
        if (!defaultColumn) return null;
        return {
          ...defaultColumn,
          visible: typeof column.visible === 'boolean' ? column.visible : defaultColumn.visible,
        };
      })
      .filter((column): column is (typeof defaultTableColumns)[number] => column !== null);

    const missing = defaultTableColumns.filter((column) => !normalized.some((item) => item.key === column.key));
    const merged = [...normalized, ...missing];
    if (merged.every((column) => !column.visible)) return defaultTableColumns;
    return merged;
  } catch {
    return defaultTableColumns;
  }
}

function getSortPresetLabel(value: RegionSortPreset) {
  return sortPresetOptions.find((option) => option.value === value)?.label ?? 'Ordre personnalisé';
}

function getRegionStatusClasses(status: RegionRecord['status']) {
  if (status === 'Active') return 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300';
  if (status === 'Inactive' || status === 'Terminé') return 'bg-slate-100 text-slate-700 dark:bg-slate-500/10 dark:text-slate-300';
  return 'bg-blue-50 text-blue-700 dark:bg-blue-500/10 dark:text-blue-300';
}

function formatRegionStatus(status: RegionRecord['status']) {
  return status === 'Terminé' ? 'Inactive' : status;
}

function getRegionSortState(preset: RegionSortPreset, columnKey: RegionTableColumnKey) {
  const [sortKey, sortDirection] = preset.split(':') as [string, 'asc' | 'desc'];

  if (sortKey === columnKey) {
    return { active: true, direction: sortDirection };
  }

  return { active: false, direction: 'asc' as const };
}

export function RegionsManagementPage({ session }: RegionsManagementPageProps) {
  const [regions, setRegions] = useState<RegionRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showLoadingSkeleton, setShowLoadingSkeleton] = useState(false);
  const [isFilterSheetOpen, setIsFilterSheetOpen] = useState(false);
  const [isToggleStatusModalOpen, setIsToggleStatusModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [regionToToggleStatus, setRegionToToggleStatus] = useState<RegionRecord | null>(null);
  const [searchValue, setSearchValue] = useState('');
  const [statusFilter, setStatusFilter] = useState<RegionStatusFilter>('all');
  const [sortPreset, setSortPreset] = useState<RegionSortPreset>('name:asc');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<(typeof pageSizeOptions)[number]>(20);
  const [tableColumns, setTableColumns] = useState<typeof defaultTableColumns>(() => readStoredTableColumns());
  const navigate = useNavigate();

  const sessionUser = session.user as { role?: string | null };
  const isCommercial = (sessionUser.role?.toLowerCase() || 'user') === 'user';

  const fetchRegions = useCallback(async () => {
    setIsLoading(true);

    try {
      const { data, error } = await (isCommercial ? listUserRegions() : listRegions());
      if (error) {
        toast.error('Erreur lors du chargement des régions');
        return;
      }

      if (data) {
        const results = data.data;
        if (isCommercial && results.length === 1) {
          navigate(`/admin/regions/${results[0].id}/salons`, { replace: true });
          return;
        }
        setRegions(results);
      }
    } finally {
      setIsLoading(false);
    }
  }, [isCommercial, navigate]);

  useEffect(() => {
    void fetchRegions();
  }, [fetchRegions]);

  useEffect(() => {
    if (!isLoading) return;
    const timeoutId = window.setTimeout(() => setShowLoadingSkeleton(true), 180);
    return () => window.clearTimeout(timeoutId);
  }, [isLoading]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(REGIONS_TABLE_COLUMNS_STORAGE_KEY, JSON.stringify(tableColumns));
  }, [tableColumns]);

  const filteredRegions = useMemo(() => {
    const normalizedSearch = searchValue.toLowerCase();

    return regions.filter((region) => {
      const matchesSearch = region.name.toLowerCase().includes(normalizedSearch) || region.code.toLowerCase().includes(normalizedSearch);
      if (!matchesSearch) return false;
      if (statusFilter !== 'all' && region.status !== statusFilter) return false;
      return true;
    });
  }, [regions, searchValue, statusFilter]);

  const sortedRegions = useMemo(() => {
    const [sortKey, sortDirection] = sortPreset.split(':') as [string, 'asc' | 'desc'];
    const direction = sortDirection === 'asc' ? 1 : -1;

    return [...filteredRegions].sort((left, right) => {
      if (sortKey === 'code') return left.code.localeCompare(right.code, 'fr', { sensitivity: 'base' }) * direction;
      if (sortKey === 'status') return left.status.localeCompare(right.status, 'fr', { sensitivity: 'base' }) * direction;
      if (sortKey === 'commercialCount') return ((left.commercialCount ?? 0) - (right.commercialCount ?? 0)) * direction;
      return left.name.localeCompare(right.name, 'fr', { sensitivity: 'base' }) * direction;
    });
  }, [filteredRegions, sortPreset]);

  const pageCount = Math.max(1, Math.ceil(sortedRegions.length / pageSize));
  const currentPage = Math.min(page, pageCount);
  const paginatedRegions = sortedRegions.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  const visibleTableColumns = tableColumns.filter((column) => column.visible);
  const paginationItems = getPaginationItems(currentPage, pageCount);
  const currentRangeStart = sortedRegions.length === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const currentRangeEnd = Math.min(currentPage * pageSize, sortedRegions.length);

  useEffect(() => {
    setPage(1);
  }, [searchValue, statusFilter, sortPreset, pageSize]);

  function resetControls() {
    setSearchValue('');
    setStatusFilter('all');
    setSortPreset('name:asc');
    setPage(1);
  }

  function resetTableColumns() {
    setTableColumns(defaultTableColumns);
  }

  function renderHeaderCell(column: (typeof defaultTableColumns)[number]) {
    if (column.key === 'name' || column.key === 'code' || column.key === 'commercialCount' || column.key === 'status') {
      const sortState = getRegionSortState(sortPreset, column.key);
      const nextPreset = (() => {
        if (column.key === 'name') {
          return sortPreset === 'name:asc' ? 'name:desc' : 'name:asc';
        }

        if (column.key === 'code') {
          return 'code:asc';
        }

        if (column.key === 'commercialCount') {
          return 'commercialCount:desc';
        }

        return 'status:asc';
      })();

      return (
        <SortHeaderButton
          active={sortState.active}
          direction={sortState.direction}
          onClick={() => setSortPreset(nextPreset)}
        >
          {column.label}
        </SortHeaderButton>
      );
    }

    return <span className="font-semibold text-foreground">{column.label}</span>;
  }

  async function downloadRegionQrCode(regionItem: RegionRecord, slotNumber: 1 | 2) {
    const response = await fetch(`${apiBaseUrl}/api/regions/${regionItem.code}/slots/${slotNumber}/qrcode`);

    if (!response.ok) {
      throw new Error('Impossible de télécharger le QR code.');
    }

    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = `qrcode-${regionItem.code.toLowerCase()}-slot-${slotNumber}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(objectUrl);
  }

  async function handleDownloadRegionQrCodes(regionItem: RegionRecord) {
    try {
      await downloadRegionQrCode(regionItem, 1);
      window.setTimeout(() => {
        void downloadRegionQrCode(regionItem, 2);
      }, 120);
      toast.success('Téléchargement des QR codes lancé.');
    } catch {
      toast.error('Impossible de télécharger les QR codes.');
    }
  }

  const activeFilters = [
    statusFilter !== 'all'
      ? {
          key: 'status',
          label: statusFilterOptions.find((option) => option.value === statusFilter)?.label ?? statusFilter,
          onClear: () => setStatusFilter('all'),
        }
      : null,
  ].filter(Boolean) as Array<{ key: string; label: string; onClear: () => void }>;

  function handleToggleStatus(regionItem: RegionRecord) {
    setRegionToToggleStatus(regionItem);
    setIsToggleStatusModalOpen(true);
  }

  async function confirmToggleStatus() {
    if (!regionToToggleStatus) return;

    const nextStatus = regionToToggleStatus.status === 'Active' ? 'Inactive' : 'Active';
    const { error } = await updateRegion(regionToToggleStatus.id, { status: nextStatus });
    if (error) {
      toast.error('Impossible de mettre à jour le statut de la région.');
    } else {
      toast.success(nextStatus === 'Inactive' ? 'Région désactivée' : 'Région réactivée');
      await fetchRegions();
    }
    setIsToggleStatusModalOpen(false);
    setRegionToToggleStatus(null);
  }

  return (
    <section className="flex min-h-[calc(100svh-10rem)] flex-col gap-6 text-foreground">
      <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-3xl font-semibold tracking-tight text-foreground">Régions</h1>
            <span className="inline-flex min-w-8 items-center justify-center rounded-full border border-border bg-card px-2.5 py-1 text-sm font-medium text-muted-foreground">
              {sortedRegions.length}
            </span>
          </div>
          <p className="text-sm text-muted-foreground">
            Consultez et gérez l’ensemble des régions enregistrées.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          {!isCommercial ? (
            <Button
              type="button"
              onClick={() => setIsCreateModalOpen(true)}
              className="h-12 border-black bg-black px-6 text-[15px] font-medium text-white shadow-none transition-colors hover:border-black/85 hover:bg-black/85 hover:text-white dark:border-border dark:bg-muted dark:text-foreground dark:hover:border-foreground/20 dark:hover:bg-muted/80"
            >
              <PlusIcon className="mr-2 size-4" />
              Ajouter une région
            </Button>
          ) : null}
        </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-[var(--radius-surface-4xl)] border border-border/70 bg-card">
        <div className="border-b border-border/70 bg-[#f4f4f538] p-4 sm:p-5 dark:bg-muted/40">
          <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
            <div className="flex w-full max-w-[42rem] items-center gap-2">
              <div className="relative min-w-0 flex-1 max-w-[34rem]">
                <SearchIcon className="pointer-events-none absolute top-1/2 left-4 size-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={searchValue}
                  onChange={(event) => setSearchValue(event.target.value)}
                  placeholder="Rechercher une région"
                  className="h-12 border-border bg-background pl-11 pr-11 text-[15px] shadow-none placeholder:text-muted-foreground dark:bg-muted"
                />
                {searchValue ? (
                  <button
                    type="button"
                    aria-label="Effacer la recherche"
                    onClick={() => setSearchValue('')}
                    className="absolute top-1/2 right-3 inline-flex size-7 -translate-y-1/2 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                  >
                    <XIcon className="size-4" />
                  </button>
                ) : null}
              </div>

              <ToolbarButton onClick={() => setIsFilterSheetOpen(true)} className="shrink-0 gap-2 px-4">
                Filtres
                <Settings2Icon className="size-4" />
              </ToolbarButton>
            </div>

            <div className="flex items-center justify-end gap-2 xl:ml-auto">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <ToolbarButton className="justify-between pr-6 xl:min-w-56">
                    {getSortPresetLabel(sortPreset)}
                    <ChevronDownIcon className="size-4" />
                  </ToolbarButton>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-64">
                  <DropdownMenuLabel>Ordre d’affichage</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuRadioGroup value={sortPreset} onValueChange={(value) => setSortPreset(value as RegionSortPreset)}>
                    {sortPresetOptions.map((option) => (
                      <DropdownMenuRadioItem key={option.value} value={option.value}>
                        {option.label}
                      </DropdownMenuRadioItem>
                    ))}
                  </DropdownMenuRadioGroup>
                </DropdownMenuContent>
              </DropdownMenu>

              <UsersTableColumnsPopover
                columns={tableColumns}
                onChange={(columns) => setTableColumns(columns as typeof defaultTableColumns)}
                onReset={resetTableColumns}
              />
            </div>
          </div>

          {activeFilters.length > 0 ? (
            <div className="mt-4 flex flex-wrap items-center gap-2">
              {activeFilters.map((filter) => (
                <button
                  key={filter.key}
                  type="button"
                  onClick={filter.onClear}
                  className="inline-flex items-center gap-2 rounded-full border border-border bg-background px-3 py-1.5 text-sm text-foreground transition-colors hover:border-foreground/20 hover:bg-muted dark:bg-muted dark:hover:bg-muted/80"
                >
                  <span>{filter.label}</span>
                  <XIcon className="size-3.5 text-muted-foreground" />
                </button>
              ))}
            </div>
          ) : null}
        </div>

        <div className="min-h-0 flex-1 overflow-auto">
          <table className="min-w-full border-separate border-spacing-0">
            <thead>
              <tr className="text-left text-sm text-muted-foreground">
                {visibleTableColumns.map((column) => (
                  <th key={column.key} className="px-6 py-5">
                    {renderHeaderCell(column)}
                  </th>
                ))}
                <th className="px-6 py-5 text-right font-semibold text-foreground">Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && showLoadingSkeleton
                ? Array.from({ length: Math.min(pageSize, 4) }).map((_, index) => (
                    <tr key={`skeleton-${index}`}>
                      {visibleTableColumns.map((column) => (
                        <td key={column.key} className="border-t border-border px-6 py-5 align-middle">
                          <Skeleton className="h-5 w-28 rounded-full" />
                        </td>
                      ))}
                      <td className="border-t border-border px-6 py-5 align-middle">
                        <Skeleton className="ml-auto h-10 w-10 rounded-full" />
                      </td>
                    </tr>
                  ))
                : paginatedRegions.map((regionItem) => (
                    <tr
                      key={regionItem.id}
                      className="group align-middle cursor-pointer transition-colors hover:bg-accent/10"
                      onClick={() => navigate(`/admin/regions/${regionItem.id}/edit`)}
                    >
                      {visibleTableColumns.map((column) => (
                        <td key={column.key} className="border-t border-border px-6 py-5 align-middle transition-colors group-hover:bg-accent/10">
                          {column.key === 'name' ? (
                            <div className="flex items-center gap-3">
                              <div className="flex size-8 items-center justify-center rounded-full bg-primary/10 text-primary">
                                <MapPinIcon className="size-4" />
                              </div>
                              <TruncatedValue className="max-w-[12rem] font-medium text-foreground">{regionItem.name}</TruncatedValue>
                            </div>
                          ) : column.key === 'code' ? (
                            <span className="text-xs font-mono text-muted-foreground">{regionItem.code}</span>
                          ) : column.key === 'commercialCount' ? (
                            <span className="text-muted-foreground">{regionItem.commercialCount || 0}</span>
                          ) : (
                            <span className={cn('inline-flex rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider', getRegionStatusClasses(regionItem.status))}>
                              {formatRegionStatus(regionItem.status)}
                            </span>
                          )}
                        </td>
                      ))}
                      <td className="border-t border-border px-6 py-5 align-middle transition-colors group-hover:bg-accent/10">
                        <div className="flex items-center justify-end">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="outline"
                                size="icon-sm"
                                className="h-10 w-10 border-border bg-background text-foreground hover:border-foreground/20 hover:bg-muted dark:bg-muted dark:hover:bg-muted/80"
                                aria-label={`Actions pour ${regionItem.name}`}
                                onClick={(event) => event.stopPropagation()}
                              >
                                <EllipsisIcon className="size-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-60">
                              <DropdownMenuLabel>Actions région</DropdownMenuLabel>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={(event) => {
                                event.stopPropagation();
                                navigate(`/admin/regions/${regionItem.id}/salons`);
                              }}>
                                <MapPinIcon />
                                Gérer les salons
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={(event) => {
                                event.stopPropagation();
                                handleDownloadRegionQrCodes(regionItem);
                              }}>
                                <DownloadIcon />
                                Télécharger les 2 QR codes
                              </DropdownMenuItem>
                              {!isCommercial ? (
                                <>
                                  <DropdownMenuItem onClick={(event) => {
                                    event.stopPropagation();
                                    navigate(`/admin/regions/${regionItem.id}/edit`);
                                  }}>
                                    <PencilIcon />
                                    Modifier
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem
                                    className={
                                      regionItem.status === 'Active'
                                        ? 'text-red-600 focus:bg-red-50 focus:text-red-600 dark:focus:bg-red-500/10'
                                        : undefined
                                    }
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      handleToggleStatus(regionItem);
                                    }}
                                  >
                                    <PowerIcon />
                                    {regionItem.status === 'Active' ? 'Désactiver' : 'Réactiver'}
                                  </DropdownMenuItem>
                                </>
                              ) : null}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </td>
                    </tr>
                  ))}
            </tbody>
          </table>
        </div>

        {!isLoading && paginatedRegions.length === 0 ? (
          <div className="flex flex-col items-center gap-3 border-t border-border px-6 py-16 text-center">
            <div className="flex size-14 items-center justify-center rounded-full bg-muted text-muted-foreground">
              <MapPinIcon className="size-6" />
            </div>
            <div className="space-y-1">
              <h3 className="text-lg font-semibold text-foreground">Aucune région ne correspond à cette vue</h3>
              <p className="max-w-md text-sm leading-6 text-muted-foreground">
                Ajuste la recherche, les filtres ou l’ordre d’affichage pour retrouver rapidement la bonne région.
              </p>
            </div>
          </div>
        ) : null}

        <div id="regions-pagination" className="grid gap-3 border-t border-border px-6 py-4 md:grid-cols-[1fr_auto_1fr] md:items-center">
          <p className="text-sm text-muted-foreground">
            {currentRangeStart}-{currentRangeEnd} sur {sortedRegions.length} régions affichées
          </p>

          <Pagination className="justify-center">
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  href="#regions-pagination"
                  aria-disabled={currentPage <= 1}
                  tabIndex={currentPage <= 1 ? -1 : 0}
                  className={cn(currentPage <= 1 ? 'pointer-events-none opacity-50' : undefined)}
                  onClick={(event) => {
                    event.preventDefault();
                    if (currentPage <= 1) return;
                    setPage((value) => value - 1);
                  }}
                />
              </PaginationItem>

              {paginationItems.map((pageNumber, index) => {
                const previousPage = paginationItems[index - 1];
                const shouldRenderEllipsis = previousPage && pageNumber - previousPage > 1;
                return (
                  <Fragment key={pageNumber}>
                    {shouldRenderEllipsis ? (
                      <PaginationItem>
                        <PaginationEllipsis />
                      </PaginationItem>
                    ) : null}
                    <PaginationItem>
                      <PaginationLink
                        href="#regions-pagination"
                        isActive={pageNumber === currentPage}
                        onClick={(event) => {
                          event.preventDefault();
                          setPage(pageNumber);
                        }}
                      >
                        {pageNumber}
                      </PaginationLink>
                    </PaginationItem>
                  </Fragment>
                );
              })}

              <PaginationItem>
                <PaginationNext
                  href="#regions-pagination"
                  aria-disabled={currentPage >= pageCount}
                  tabIndex={currentPage >= pageCount ? -1 : 0}
                  className={cn(currentPage >= pageCount ? 'pointer-events-none opacity-50' : undefined)}
                  onClick={(event) => {
                    event.preventDefault();
                    if (currentPage >= pageCount) return;
                    setPage((value) => value + 1);
                  }}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>

          <div className="flex justify-start md:justify-end">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <ToolbarButton className="h-10 justify-between pl-4 pr-5 text-sm">
                  {pageSize} lignes
                  <ChevronDownIcon className="size-4" />
                </ToolbarButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuLabel>Lignes par page</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuRadioGroup value={String(pageSize)} onValueChange={(value) => setPageSize(Number(value) as (typeof pageSizeOptions)[number])}>
                  {pageSizeOptions.map((option) => (
                    <DropdownMenuRadioItem key={option} value={String(option)}>
                      {option} lignes
                    </DropdownMenuRadioItem>
                  ))}
                </DropdownMenuRadioGroup>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      <ConfirmationModal
        isOpen={isToggleStatusModalOpen}
        onClose={() => {
          setIsToggleStatusModalOpen(false);
          setRegionToToggleStatus(null);
        }}
        onConfirm={confirmToggleStatus}
        title={regionToToggleStatus?.status === 'Active' ? 'Désactiver la région ?' : 'Réactiver la région ?'}
        description={
          regionToToggleStatus
            ? regionToToggleStatus.status === 'Active'
              ? `Voulez-vous vraiment désactiver la région "${regionToToggleStatus.name}" ?`
              : `Voulez-vous vraiment réactiver la région "${regionToToggleStatus.name}" ?`
            : ''
        }
        confirmText={regionToToggleStatus?.status === 'Active' ? 'Désactiver' : 'Réactiver'}
        variant="default"
      />

      <Dialog open={isCreateModalOpen} onOpenChange={setIsCreateModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ajouter une région</DialogTitle>
            <DialogDescription>
              Créez une nouvelle région sans quitter la liste.
            </DialogDescription>
          </DialogHeader>

          <div className="px-6 py-6">
            <RegionForm
              surface="embedded"
              onCancel={() => setIsCreateModalOpen(false)}
              onSuccess={async () => {
                setIsCreateModalOpen(false);
                await fetchRegions();
              }}
            />
          </div>
        </DialogContent>
      </Dialog>

      <Sheet open={isFilterSheetOpen} onOpenChange={setIsFilterSheetOpen}>
        <SheetContent side="right" className="w-full border-border sm:max-w-md">
          <SheetHeader className="border-b border-border">
            <SheetTitle>Filtres</SheetTitle>
            <SheetDescription>
              Ajuste le statut des régions sans quitter la table.
            </SheetDescription>
          </SheetHeader>

          <div className="flex-1 space-y-8 overflow-y-auto px-6 py-6">
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-foreground">Statut</h3>
              <div className="flex flex-wrap gap-2">
                {statusFilterOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setStatusFilter(option.value)}
                    className={cn(
                      'rounded-full border px-4 py-2 text-sm transition-colors',
                      statusFilter === option.value
                        ? 'border-black bg-black text-white'
                        : 'border-border bg-background text-foreground hover:border-foreground/20 hover:bg-muted',
                    )}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <SheetFooter className="border-t border-border">
            <Button variant="outline" onClick={resetControls}>
              Réinitialiser
            </Button>
            <Button onClick={() => setIsFilterSheetOpen(false)}>Appliquer</Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </section>
  );
}
